import { useLayoutEffect, useRef, useState } from "preact/hooks";

import { readReplyContent } from "@go-novus/admin-api";

import type { ConversationState } from "../conversation/conversation";
import { CHARACTER_DONE_MS } from "./widget-character";

export type CharacterReaction = "ack" | "done";

/**
 * 角色的一次性反应：买家发出消息时生成中那一行的角色回应一下；本次挂载后新到的普通回复，收起入口播一次完成动作
 * （聊天窗里的角色改成说话、说完眨眼，跟着正文流出走，见 widget-message）。
 * 挂载时已有的消息算历史；页面在后台时不播，回到前台也不补。
 * 放在抽屉和收起入口之外，手机上两处共用同一份反应，开合时一起打断。
 */
export function useCharacterReaction(
  state: ConversationState,
): CharacterReaction | undefined {
  const seen = useRef(new Set(state.messages.map((message) => message.id)));
  const previous = useRef(state);
  const [reaction, setReaction] = useState<CharacterReaction>();

  useLayoutEffect(() => {
    const before = previous.current;
    previous.current = state;
    const fresh = state.messages.filter(
      (message) => !seen.current.has(message.id),
    );
    for (const message of fresh) seen.current.add(message.id);
    // ack 补写服务端消息标识只更新元数据，不打断这轮发送的回应动作。
    if (
      state.busy &&
      before.busy &&
      state.open === before.open &&
      state.error === before.error &&
      fresh.length === 0
    )
      return;
    // 发送在同一次更新里进入处理并改动消息；待答恢复只进入处理，不算买家刚发。
    const sent =
      state.busy && !before.busy && state.messages !== before.messages;
    // 本地原文（主动提示）不是回复；转人工保持中性，不播完成动作。
    const replied = fresh.some(
      (message) =>
        message.role === "ai" &&
        message.textFormat !== "plain" &&
        !readReplyContent(message.text).blocks.some(
          (block) => block.type === "transfer",
        ),
    );
    // 开合、发送、失败都会走到这里重新判断，没有新反应就打断正在演的那个。
    const next = document.hidden
      ? undefined
      : sent
        ? "ack"
        : replied
          ? "done"
          : undefined;
    setReaction(next);
    if (next !== "done") return;
    const timer = setTimeout(() => {
      setReaction(undefined);
    }, CHARACTER_DONE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [state.busy, state.error, state.messages, state.open]);

  return reaction;
}
